import React, { useCallback, useRef, useState } from 'react';
import { IonContent, IonInfiniteScroll, IonInfiniteScrollContent, IonPage, useIonViewWillEnter } from '@ionic/react';
import {
  Container,
  FlagTypes,
  ReviewCard,
  ReviewStatistics,
  withSpecialistScheduleContext,
} from '@mindly/ui-components';

import ChevronHeader from '../../components/ChevronHeader/ChevronHeader';
import { useIntl } from 'react-intl';
import { RouteComponentProps, useHistory } from 'react-router';
import { SpecialistReviewsContainer } from './SpecialistReviews.style';
import { useDispatch, useSelector } from 'react-redux';
import { reviewIsInfinitySelector, specialistReviewsSelector } from '../../redux/specialistReviews/selector';
import { fetchReviews } from '../../redux/specialistReviews/thunk';
import AppointmentFooter from '../../components/AppointmentFooter/AppointmentFooter';
import { userSelector } from '../../redux/user/selector';
import { IonInfiniteScrollCustomEvent } from '@ionic/core/dist/types/components';
import { psychologistSelectorById, specialistsSelector } from '../../redux/specialists/selector';
import { loadConsultationsThunk } from '../../redux/consultations/action';
import { fetchSubscriptions } from '../../redux/subscriptions/thunk';
import useLangs from '../../context/LangsContext';
import { PrivateRoutes } from '../../routes/navigation';
import { resetOffset, resetSpecialistReview } from '../../redux/specialistReviews/reducer';
import useToast from '../../context/ToastContext/ToastContext';
import { fetchSpecialistsList } from '../../redux/specialists/thunk';

interface SpecialistReviewsProps
  extends RouteComponentProps<{
    id: string;
  }> {}

const SpecialistReviews: React.FC<SpecialistReviewsProps> = ({ match }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const { showToast } = useToast();
  const pageRef = useRef();

  const { id } = match.params;
  const { locale } = useLangs();

  const [isLoading, setIsLoading] = useState(true);
  const [hideTabBar, setHideTabBar] = useState(false);

  const reviews = useSelector(specialistReviewsSelector(id));
  const user = useSelector(userSelector);
  const specialist = useSelector(psychologistSelectorById(id));
  const specialistList = useSelector(specialistsSelector);
  const isInfiniteDisabled = useSelector(reviewIsInfinitySelector(id));

  const onIonInfinite = useCallback(
    (event: IonInfiniteScrollCustomEvent<void>) => {
      if (!user?.id) return event.target.complete();
      dispatch(fetchReviews({ specialistID: id }))
        .unwrap()
        .finally(() => {
          event.target.complete();
        });
    },
    [user?.id]
  );

  useIonViewWillEnter(async () => {
    try {
      if (!user?.id) return;
      const params = new URLSearchParams(window.location.search);
      const hideTabBarQuery = params.get('hideTabBar');
      setHideTabBar(!!hideTabBarQuery);

      setIsLoading(true);
      dispatch(resetOffset(id));
      dispatch(resetSpecialistReview(id));
      !specialistList?.length && (await dispatch(fetchSpecialistsList(locale)));

      const [localReviews] = await Promise.all([
        dispatch(fetchReviews({ specialistID: id })).unwrap(),
        dispatch(loadConsultationsThunk(user?.id)),
        dispatch(fetchSubscriptions(user?.id)),
      ]);

      if (!localReviews) history.push(PrivateRoutes.PSYCHOLOGIST_LIST);
    } catch (error) {
      showToast({ error });
      history.push(PrivateRoutes.PSYCHOLOGIST_PROFILE.replace(':id', id));
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  return (
    <IonPage ref={pageRef}>
      <ChevronHeader
        title={intl.formatMessage({
          id: 'specialistReviews.title',
          defaultMessage: 'Відгуки',
        })}
        isLoading={isLoading}
      />
      <IonContent>
        <Container>
          <SpecialistReviewsContainer>
            <ReviewStatistics
              averageStarsCount={specialist?.reviews_average_rating ?? 0}
              countOfReviews={specialist?.reviews_count ?? 0}
              statistics={
                specialist?.reviews_count_by_stars ?? {
                  '5': 5,
                  '4': 4,
                  '3': 3,
                  '2': 2,
                  '1': 1,
                }
              }
              loading={isLoading}
              translations={{
                reviews: [
                  intl.formatMessage({
                    id: 'other.specialistReviews.one',
                    defaultMessage: 'відгук',
                  }),
                  intl.formatMessage({
                    id: 'other.specialistReviews.many',
                    defaultMessage: 'відгуки',
                  }),
                  intl.formatMessage({
                    id: 'other.specialistReviews.several',
                    defaultMessage: 'відгуків',
                  }),
                ],
              }}
            />

            {!isLoading
              ? reviews?.map((review, index) => (
                  <ReviewCard
                    key={`${review?.id}_${index}`}
                    reviewerAvatar={review?.client_avatar ?? ''}
                    reviewerFirstName={review?.client_first_name ?? ''}
                    reviewerLastName={review?.client_last_name ?? ''}
                    reviewerCountry={(review?.client_country ?? 'uk') as keyof typeof FlagTypes}
                    reviewedAtInSeconds={review?.created_at}
                    starsCount={review?.review_rating ?? 5}
                    reviewText={review?.text_of_review ?? undefined}
                    cutReview={false}
                    type={'list'}
                    translations={{
                      showMore: intl.formatMessage({
                        id: 'general.showMore.btn',
                        defaultMessage: 'Показати більше',
                      }),
                      showAllReviews: intl.formatMessage({
                        id: 'specialistProfile.reviews.showAll',
                        defaultMessage: 'Показати всі відгуки',
                      }),
                    }}
                  />
                ))
              : Array.from({ length: 6 }).map((_, index) => (
                  <ReviewCard
                    key={`${id}_${index}`}
                    reviewerAvatar={''}
                    reviewerFirstName={''}
                    reviewerLastName={''}
                    translations={{
                      showMore: 'Показати більше',
                      showAllReviews: 'Показати всі відгуки',
                    }}
                    loading={true}
                    starsCount={5}
                    type={'list'}
                  />
                ))}
          </SpecialistReviewsContainer>
        </Container>
        <IonInfiniteScroll onIonInfinite={onIonInfinite} threshold="100%" disabled={isInfiniteDisabled}>
          <IonInfiniteScrollContent />
        </IonInfiniteScroll>
      </IonContent>
      {!hideTabBar && (
        <AppointmentFooter
          specialistFullName={`${specialist?.first_name} ${specialist?.last_name ? `${specialist?.last_name?.[0]}.` : ''}`}
          specialistID={id}
          loading={isLoading}
          presentingElement={pageRef.current}
        />
      )}
    </IonPage>
  );
};

export default withSpecialistScheduleContext(SpecialistReviews);
